import React, { useEffect, useState } from 'react';
import { Bar, Pie } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    ChartOptions,
    CategoryScale,
    LinearScale,
    BarElement,
    ArcElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import { ChartType } from './ChartType';
import { IChartData } from './IChartData';
import { httpGet } from '../../../modules/Backend';
import { HttpStatusCode } from 'axios';
import * as Dom from '../../../modules/Dom';
import { displayDateTime } from '../../../helpers/DateHelper';
import { getChartDefaults } from './ChartDefaults';

// Register required modules for Chart.js
ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Title, Tooltip, Legend);

interface ChartRendererProps {
    chartType: string | null | undefined;
    entityName?: string;
    parentId: string | undefined;
    options?: any;
    endpoint?: string;
    onMetadataLoaded?: (metadata: Record<string, any>) => void;
}

// Helper function to check if labels are years (numeric strings)
function areYearLabels(labels: string[]): boolean {
    if (!labels || labels.length === 0) return false;
    // Check if all labels are 4-digit year strings
    return labels.every(label => /^\d{4}$/.test(label));
}

const ChartRenderer: React.FC<ChartRendererProps> = ({ chartType, entityName, parentId, options, endpoint, onMetadataLoaded }: ChartRendererProps) => {
    const [chartData, setChartData] = useState<IChartData | undefined>(undefined);
    const [loading, setLoading] = useState(false);
    const [defaultOptions, setDefaultOptions] = useState<ChartOptions | undefined>(undefined);

    useEffect(() => {
        async function getData() {
            setLoading(true);
            const startTime = Date.now(); // Record the start time

            const colours = Dom.getColoursFromRoot();
            ChartJS.defaults.color = colours.textPrimary;

            const urlParams = new URLSearchParams();
            urlParams.append('parentId', parentId!);

            const apiEndpoint = endpoint || `api/${entityName}/chartData`;
            const response = await httpGet(`${apiEndpoint}?${urlParams.toString()}`);
            if (response.status === HttpStatusCode.Ok) {
                const responseData = response.data.chartData;
                const chartData = configureChartData(responseData, colours, endpoint);
                setChartData(chartData);
                const chartOptions = configureChartOptions(chartData, colours, responseData.asOf, endpoint);
                setDefaultOptions(chartOptions);
                
                if (response.data.metadata && onMetadataLoaded) {
                    onMetadataLoaded(response.data.metadata);
                }
            }
            const elapsedTime = Date.now() - startTime;
            const remainingTime = 250 - elapsedTime; // Calculate remaining time to meet minimum load duration

            if (remainingTime > 0) {
                setTimeout(() => setLoading(false), remainingTime);
            } else {
                setLoading(false);
            }
        }

        if (parentId && (entityName || endpoint)) {
            getData();
        }
    }, [entityName, parentId, endpoint]);

    function configureChartData(responseData: any, colours: Record<string, string>, endpoint?: string) {
        // Check if this is a "By Year" chart - use wider bars for year charts
        const isYearChart = endpoint?.includes('chartDataByYear');
        
        const enhancedDatasets = responseData.datasets.map((dataset: any) => ({
            ...dataset,
            backgroundColor: colours.secondaryTint,
            borderColor: colours.secondary,
            // Use larger maxBarThickness for year charts to make bars wider
            maxBarThickness: isYearChart ? 400 : 250,
            // In Chart.js v3+, categoryPercentage and barPercentage are set on the dataset, not the scale
            categoryPercentage: isYearChart ? 0.95 : 0.8,
            barPercentage: isYearChart ? 0.95 : 0.9,
        }));

        const chartDisplayData = {
            labels: responseData.labels,
            datasets: enhancedDatasets,
        };
        return chartDisplayData;
    }

    function configureChartOptions(chartData: IChartData, colours: Record<string, string>, asOf?: Date, endpoint?: string): ChartOptions {
        const hasData = chartData.datasets.some(x => x.data.length > 0);
        const baseOptions = getChartDefaults(hasData);
        baseOptions!.plugins!.tooltip!.backgroundColor = colours.primary;
        baseOptions!.plugins!.tooltip!.titleColor = colours.white;
        baseOptions!.plugins!.tooltip!.bodyColor = colours.white;
        baseOptions!.plugins!.tooltip!.borderColor = colours.primary;
        if (asOf) {
            baseOptions!.plugins!.title!.text = `Data as of: ${displayDateTime(asOf)}, updates once per day`;
        }
        else {
            baseOptions!.plugins!.title!.text = `Data generated daily, at midnight.`
        }
        baseOptions!.plugins!.title!.color = colours.textSecondary;
        baseOptions!.plugins!.legend!.labels = { color: colours.textPrimary };

        baseOptions!.interaction = {
            mode: 'index',
            intersect: false,
        };

        // Check if this is a "By Year" chart or if labels are years
        const isYearChart = endpoint?.includes('chartDataByYear') || areYearLabels(chartData.labels);
        const labelCount = chartData.labels.length;
        
        // Create a closure to capture labels for the callback
        const labels = chartData.labels;

        // Configure x-axis ticks based on chart type
        const xAxisConfig: any = {
            grid: {
                display: false,
            },
        };

        if (isYearChart && labelCount > 20) {
            // For year charts with many labels, show labels at regular intervals by index
            // Calculate step size: show approximately 15-20 labels max
            const stepSize = Math.max(1, Math.floor(labelCount / 15));
            
            xAxisConfig.ticks = {
                autoSkip: false, // We'll control skipping via callback
                maxRotation: 45,
                minRotation: 45,
                callback: function(value: any, index: number) {
                    // Always show first and last labels
                    if (index === 0 || index === labelCount - 1) {
                        return labels[index];
                    }
                    // Show labels at regular intervals based on index (not year value)
                    if (index % stepSize === 0) {
                        return labels[index];
                    }
                    // Return empty string to hide this label
                    return '';
                },
            };
        } else {
            // For other charts, show all labels
            xAxisConfig.ticks = {
                autoSkip: false,
            };
        }

        baseOptions!.scales = {
            x: xAxisConfig,
            y: {
                beginAtZero: true,
            },
        };

        baseOptions!.elements = {
            bar: {                
                borderRadius: 8,
            },
        };

        return baseOptions;
    }

    return (
        <div className='chart__renderer'>
            <div className={`chart__canvas ${loading ? "chart__canvas--loading" : ""}`}>
                {loading && <div className="loaders loaders__full" />}
                {!loading && chartData && chartType === ChartType.Bar && (
                    <Bar data={chartData} options={options || defaultOptions} />
                )}
                {!loading && chartData && chartType === ChartType.Pie && (
                    <Pie data={chartData} options={options || defaultOptions} />
                )}
            </div>
        </div>
    );
};

export default ChartRenderer;

